import {
  Campaign,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  CampaignFilters,
  CampaignListResponse
} from '../types/analytics';
import { ANALYTICS_API_URL } from '../constants/config';
import { getAndSanitize } from '../shared/utils/sanitization';

export class CampaignService {
  private baseUrl = `${ANALYTICS_API_URL}/api/campaigns`;

  private getAuthHeaders() {
    const token = getAndSanitize('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Crear una nueva campaña
   */
  async createCampaign(data: CreateCampaignRequest): Promise<Campaign> {
    try {
      const adminId = getAndSanitize('userId');
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          ...data,
          adminId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear campaña');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('[CampaignService] Error creating campaign:', error);
      throw error;
    }
  }

  /**
   * Obtener lista de campañas
   */
  async getCampaigns(filters: CampaignFilters = {}): Promise<CampaignListResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (filters.status) queryParams.append('status', filters.status);
      if (filters.objective) queryParams.append('objective', filters.objective);
      if (filters.createdBy) queryParams.append('createdBy', filters.createdBy);
      if (filters.dateRange?.start) queryParams.append('dateRangeStart', filters.dateRange.start);
      if (filters.dateRange?.end) queryParams.append('dateRangeEnd', filters.dateRange.end);
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());

      const url = queryParams.toString() ? `${this.baseUrl}?${queryParams}` : this.baseUrl;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al obtener campañas');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('[CampaignService] Error getting campaigns:', error);
      throw error;
    }
  }

  /**
   * Obtener una campaña por ID
   */
  async getCampaignById(id: string): Promise<Campaign> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al obtener campaña');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('[CampaignService] Error getting campaign by ID:', error);
      throw error;
    }
  }

  /**
   * Actualizar una campaña
   */
  async updateCampaign(id: string, data: UpdateCampaignRequest): Promise<Campaign> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar campaña');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('[CampaignService] Error updating campaign:', error);
      throw error;
    }
  }

  /**
   * Eliminar una campaña
   */
  async deleteCampaign(id: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al eliminar campaña');
      }
    } catch (error) {
      console.error('[CampaignService] Error deleting campaign:', error);
      throw error;
    }
  }

  /**
   * Agregar usuarios objetivo a una campaña
   */
  async addTargetedUsers(
    campaignId: string, 
    users: Array<{ userId: string; username: string; phone?: string }>
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/${campaignId}/users`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ users }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al agregar usuarios a la campaña');
      }
    } catch (error) {
      console.error('[CampaignService] Error adding targeted users:', error);
      throw error;
    }
  }

  /**
   * Actualizar estado de una campaña
   */
  async updateCampaignStatus(
    id: string, 
    status: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'
  ): Promise<Campaign> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}/status`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar estado de campaña');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('[CampaignService] Error updating campaign status:', error);
      throw error;
    }
  }
}

// Instancia singleton del servicio
export const campaignService = new CampaignService();